<template>
  <v-dialog v-model="loginDialog" persistent max-width="450px">
    <v-card>
      <v-card-title
        class="d-flex justify-space-between align-center"
        style="background-color: #666"
      >
        <div class="text-h5 text-medium-emphasis ps-2">
          <img
            src="@/assets/images/main-logo.png"
            alt="WhaleWisdom logo"
            style="max-height: 50px"
          />
        </div>

        <v-btn :icon="mdiClose" variant="text" color="white" @click="loginDialog = false"></v-btn>
      </v-card-title>

      <!-- Tabs: Log In / Sign Up -->
      <v-tabs v-model="currentTab" grow>
        <v-tab value="log-in-tab"> Log In </v-tab>
        <v-tab value="sign-up-tab"> Sign Up </v-tab>
      </v-tabs>
      <v-tabs-window v-model="currentTab" touchless class="py-1">
        <!-- Log In -->
        <v-tabs-window-item value="log-in-tab">
          <v-card-text>
            <v-form
              ref="logInForm"
              v-model="validLogIn"
              method="POST"
              action="/sessions/create_next"
              @submit.prevent="logIn()"
            >
              <input name="recaptcha" type="hidden" :value="recaptcha_value" />
              <input name="return_to" type="hidden" :value="return_to" />
              <v-text-field
                id="username"
                ref="email"
                v-model.trim="logInFields.email"
                name="login"
                variant="outlined"
                :rules="[rules.required]"
                :prepend-icon="mdiEmail"
                autocomplete="username"
                label="E-mail/Username*"
                @keyup.enter="logIn()"
              />
              <v-text-field
                id="current-password"
                v-model.trim="logInFields.password"
                name="password"
                :rules="[rules.required]"
                variant="outlined"
                :type="logInFields.showPassword ? 'text' : 'password'"
                :append-icon="logInFields.showPassword ? mdiEyeOff : mdiEye"
                label="Password*"
                autocomplete="current-password"
                :prepend-icon="mdiLock"
                @click:append="logInFields.showPassword = !logInFields.showPassword"
                @keyup.enter="logIn()"
              />
              <input name="remember_me" :value="remember_me" type="hidden" />
              <v-checkbox v-model="logInFields.rememberMe" label="Remember me" />
            </v-form>
            <p>
              <a href="https://whalewisdom.com/forgot_password">Forgot your password?</a>
            </p>
            <v-alert v-model="logInFields.error" type="error" class="mt-4">
              {{ logInFields.error }}
            </v-alert>
          </v-card-text>
        </v-tabs-window-item>

        <!-- Sign Up -->
        <v-tabs-window-item value="sign-up-tab">
          <v-card-text v-show="signUpCompleted">
            <h4 class="text-subtitle-1 font-weight-medium mb-6">Thanks for signing up!</h4>
            <p>
              Click the verification link in the e-mail we sent, to validate your account before
              logging in.
            </p>
          </v-card-text>

          <v-card-text v-show="!signUpCompleted">
            <div class="mb-6 text-body-1">
              <p class="text-h6">Creating an account on WhaleWisdom is free and easy.</p>
              <p>
                While much of WhaleWisdom doesn't require an account, creating one makes using the
                site easier...
              </p>
              <ul class="pl-4">
                <li>Save and edit your e-mail alerts</li>
                <li>Create and track stocks</li>
                <li>
                  Create groups of funds to run backtests against and save your favorite backtests
                </li>
                <li>
                  Premium subscriptions are available to registered users with many more
                  <a href="/pricing" target="_blank">features</a>
                </li>
              </ul>
            </div>

            <v-form ref="signUpForm" v-model="validSignUp" @submit.prevent="signUp()">
              <v-text-field
                v-model.trim="signUpFields.email"
                :rules="[rules.required, rules.email]"
                autocorrect="email"
                spellcheck="false"
                variant="outlined"
                :prepend-icon="mdiEmail"
                label="E-mail*"
                @keyup.enter="signUp()"
              />

              <v-text-field
                id="new-password"
                v-model.trim="signUpFields.password"
                :rules="[rules.required, rules.password]"
                :type="signUpFields.showPassword ? 'text' : 'password'"
                :append-icon="signUpFields.showPassword ? mdiEyeOff : mdiEye"
                label="Password*"
                variant="outlined"
                autocomplete="new-password"
                :prepend-icon="mdiLock"
                @click:append="signUpFields.showPassword = !signUpFields.showPassword"
                @keyup.enter="signUp()"
              />
              <v-text-field
                v-model.trim="signUpFields.passwordConfirm"
                :rules="[rules.required, rules.password, rules.confirmMatch]"
                :type="signUpFields.showPassword ? 'text' : 'password'"
                :append-icon="signUpFields.showPassword ? mdiEyeOff : mdiEye"
                label="Confirm password*"
                variant="outlined"
                :prepend-icon="mdiLockOutline"
                @click:append="signUpFields.showPassword = !signUpFields.showPassword"
                @keyup.enter="signUp()"
              />
              <v-checkbox
                v-model="signUpFields.quarterly"
                density="compact"
                hide-details
                variant="outlined"
                class="ma-0 pa-0"
              >
                <template #label>
                  <div>
                    Get Free
                    <a
                      target="_blank"
                      href="http://cdn.whalewisdom.com/newsletter/WhaleWisdom-2020-Q1.pdf"
                    >
                      Quarterly 13F Analysis</a
                    >
                  </div>
                </template>
              </v-checkbox>
              <v-checkbox
                v-model="signUpFields.weekly"
                density="compact"
                hide-details
                class="ma-0 pa-0"
                label="Get the Free Weekly Newsletter"
              />
            </v-form>
            <v-alert v-model="signUpFields.error" type="error" class="mt-4">
              {{ signUpFields.error }}
            </v-alert>
          </v-card-text>
        </v-tabs-window-item>
      </v-tabs-window>

      <!-- Button -->
      <v-card-actions class="pa-0">
          <v-btn
            v-if="currentTab === 'log-in-tab'"
            :loading="loading"
            :disabled="loading || !validLogIn"
            size="large"
            block
            color="primary"
            class="ma-0"
            style="border-radius: 0; height: 56px"
            @click.stop="logIn()"
          >
            Log in
            <v-icon>{{ mdiChevronRight }}</v-icon>
          </v-btn>

          <v-btn
            v-if="currentTab === 'sign-up-tab' && !signUpCompleted"
            :loading="loading"
            :disabled="loading || !validSignUp"
            size="large"
            block
            color="primary"
            class="ma-0"
            style="border-radius: 0; height: 56px"
            @click.stop="signUp()"
          >
            Sign Up
            <v-icon>{{ mdiChevronRight }}</v-icon>
          </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import {
  mdiClose,
  mdiEmail,
  mdiEyeOff,
  mdiEye,
  mdiLock,
  mdiLockOutline,
  mdiChevronRight
} from '@mdi/js'
// Modules
//import { apiRoot } from '@/store/api'
import { useUserStore, useAppStore, useWwStore } from '@/store'
import { mapWritableState } from 'pinia'
import { loadScript } from 'vue-plugin-load-script'
import { apiRoot } from '@/store/api'

export default {
  data() {
    return {
      mdiClose,
      mdiEmail,
      mdiEyeOff,
      mdiEye,
      mdiLock,
      mdiLockOutline,
      mdiChevronRight,
      script_loaded: false,
      currentTab: 'log-in-tab',
      recaptcha_loaded: false,
      signUpCompleted: false,
      loading: false,
      validLogIn: true,
      validSignUp: true,
      return_to: null,
      recaptcha_value: '',
      remember_me: null,
      logInFields: {
        email: null,
        password: null,
        rememberMe: false,
        showPassword: false,
        error: null
      },
      signUpFields: {
        email: null,
        password: null,
        passwordConfirm: null,
        showPassword: false,
        error: false,
        quarterly: true,
        weekly: true
      },
      rules: {
        required: value => !!value || 'This field is required',
        email: value => {
          const pattern =
            /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail'
        },
        password: value =>
          (value && value.length >= 4 && value.length <= 25) ||
          'Password must be 4-25 characters long',
        confirmMatch: () =>
          this.signUpFields.password === this.signUpFields.passwordConfirm ||
          'Passwords do not match'
      }
    }
  },

  computed: {
    ...mapWritableState(useAppStore, ['loginDialog']),
    ...mapWritableState(useUserStore, ['user'])
  },

  watch: {
    loginDialog(isVisible) {
      if (isVisible) {
        this.signUpCompleted = false
        this.currentTab === 'log-in-tab' && this.$nextTick(() => this.$refs.email.focus())
      } else {
        // this.$refs.logInForm.reset()
        // this.$refs.signUpForm.reset()
      }
    }
  },

  mounted() {},
  beforeUnmount() {
    if (!this.user) {
      // this.$recaptcha.destroy()
    }
  },
  methods: {
    async load_recaptcha() {
      await loadScript(
        'https://www.google.com/recaptcha/enterprise.js?render=' +
          '6LcgXrQlAAAAAC2H213o2fkTLF52wruER2HUsS80'
      )
        .then(() => {
          // Script is loaded, do something
          this.script_loaded = true
        })
        .catch(() => {
          // Failed to fetch script
        })
    },
    async logIn() {
      if (!this.$refs.logInForm.validate()) return
      this.logInFields.error = null
      if (!this.script_loaded) await this.load_recaptcha()
      const that = this
      this.loading = true
      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise
          .execute('6LcgXrQlAAAAAC2H213o2fkTLF52wruER2HUsS80', {
            action: 'login'
          })
          .then(function (token) {
            that.recaptcha_value = token
            that.return_to = window.location
            if (that.logInFields.rememberMe) that.remember_me = 'true'
            that.$nextTick(() => {
              console.log(that.$refs.logInForm)
              that.$refs.logInForm.$el.submit()
            })
            /*that.$axios
              .post("sessions/dashboard_login", {
                recaptcha: token,
                //params: {
                login: that.logInFields.email,
                password: that.logInFields.password,
                remember_me: that.logInFields.rememberMe,
                //  }
              })
              .then((response) => {
                that.user = response.data;
                that.loginDialog = false;
              })
              .catch((error) => {
                let msg = "";
                if (error.response && error.response.message)
                  msg = error.response.message;
                else if (error.response && error.response.data)
                  msg = error.response.data;
                else if (error.message) msg = error.message;
                else msg = "Log in failed. Check your login name and password.";
                that.logInFields.error = msg;
                that.loading = false;
              });*/
          })
          .catch(err => {
            console.log(err)
            that.loading = false
          })
      })
    },

    async signUp() {
      if (!this.$refs.signUpForm.validate()) return
      this.loading = true
      this.signUpFields.error = null
      if (!this.script_loaded) await this.load_recaptcha()
      const that = this
      window.grecaptcha.enterprise.ready(() => {
        window.grecaptcha.enterprise
          .execute('6LcgXrQlAAAAAC2H213o2fkTLF52wruER2HUsS80', {
            action: 'db_create_user'
          })
          .then(function (token) {
            apiRoot
              .post('users/create_ajax', {
                recaptcha: token,
                user: {
                  email: that.signUpFields.email,
                  password: that.signUpFields.password,
                  password_confirmation: that.signUpFields.passwordConfirm
                },
                weekly: that.signUpFields.weekly,
                quarterly: that.signUpFields.quarterly
              })
              .then(() => {
                that.signUpCompleted = true
                that.loading = false
              })
              .catch(error => {
                that.signUpFields.error =
                  (error.response && error.response.data) ||
                  error.message ||
                  'Sign up failed. Verify that your e-mail is correct and your password is between 4-25 characters.'
                that.loading = false
              })
          })
          .catch(err => {
            console.log(err)
            that.loading = false
          })
      })
    }
  }
}
</script>
